import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { API_URL } from '../lib/utils';
import { toast } from 'sonner';
import {
  BadgeCheck, Search, Loader2, ExternalLink, Download, Clock, ShieldCheck,
  AlertTriangle, Trash2, Edit3, History, Upload, RefreshCw,
} from 'lucide-react';

const LICENSE_TYPES = ['LCSW', 'LMSW', 'LBSW', 'SWA'];
const V_STATUSES = ['pending', 'verified', 'suspended', 'revoked', 'unable_to_verify', 'expired', 'inactive'];
const L_STATUSES = ['active', 'inactive', 'expired', 'suspended', 'revoked', 'pending'];

const VISUAL = {
  green: { cls: 'bg-emerald-100 text-emerald-700', icon: ShieldCheck, label: 'Verified · Active' },
  yellow: { cls: 'bg-amber-100 text-amber-700', icon: Clock, label: 'Renewal due <60d' },
  gray: { cls: 'bg-gray-100 text-gray-600', icon: Clock, label: 'Pending / Overdue' },
  red: { cls: 'bg-red-100 text-red-700', icon: AlertTriangle, label: 'Expired / Inactive' },
};

const emptyForm = {
  user_id: '', applicant_name: '', applicant_email: '',
  legal_first_name: '', legal_middle_name: '', legal_last_name: '',
  state: '', profession: 'Social Worker', license_type: '', license_number: '',
  license_status: 'active', original_issue_date: '', current_rank_effective_date: '',
  expiration_date: '', supervisor_designation: '', disciplinary_action_indicator: false,
  disciplinary_action_url: '', official_source_name: '', official_source_url: '',
  verification_method: 'manual', verification_status: 'pending', admin_notes: '',
  next_reverification_date: '', display_full_number: false,
};

const AdminTabVerification = () => {
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ verification_status: 'all', state: '', license_type: 'all', search: '', expiring_days: 'all', overdue: false });

  const [applicantQuery, setApplicantQuery] = useState('');
  const [applicants, setApplicants] = useState([]);
  const [searching, setSearching] = useState(false);

  const [editorOpen, setEditorOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [audit, setAudit] = useState([]);

  const [txUrl, setTxUrl] = useState('https://www.bhec.texas.gov/csv/SW.csv');
  const [txImporting, setTxImporting] = useState(false);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 50 };
      if (filters.verification_status !== 'all') params.verification_status = filters.verification_status;
      if (filters.license_type !== 'all') params.license_type = filters.license_type;
      if (filters.state) params.state = filters.state;
      if (filters.search) params.search = filters.search;
      if (filters.expiring_days !== 'all') params.expiring_days = filters.expiring_days;
      if (filters.overdue) params.overdue = true;
      const res = await axios.get(`${API_URL}/admin/license-verifications`, { params });
      setRows(res.data.verifications);
      setSummary(res.data.summary);
    } catch (e) {
      toast.error('Failed to load verifications');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const searchApplicants = async () => {
    setSearching(true);
    try {
      const res = await axios.get(`${API_URL}/admin/license-applicants`, { params: { q: applicantQuery, limit: 15 } });
      setApplicants(res.data.applicants);
    } catch (e) {
      toast.error('Search failed');
    } finally {
      setSearching(false);
    }
  };

  const openEditorForApplicant = (a) => {
    const v = a.verification;
    if (v) return openEditorForVerification(v);
    setForm({ ...emptyForm, user_id: a.user_id, applicant_name: a.name, applicant_email: a.email, license_type: a.license_type || '', state: a.location || '' });
    setEditingId(null);
    setAudit([]);
    setEditorOpen(true);
  };

  const openEditorForVerification = async (v) => {
    setForm({ ...emptyForm, ...v, user_id: v.user_id });
    setEditingId(v.verification_id);
    setEditorOpen(true);
    try {
      const res = await axios.get(`${API_URL}/admin/license-verifications/${v.verification_id}`);
      setAudit(res.data.audit || []);
    } catch { setAudit([]); }
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API_URL}/admin/license-verifications/${editingId}`, form);
        toast.success('Verification updated');
      } else {
        await axios.post(`${API_URL}/admin/license-verifications`, form);
        toast.success('Verification saved');
      }
      setEditorOpen(false);
      fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const quickStatus = async (v, status) => {
    try {
      await axios.put(`${API_URL}/admin/license-verifications/${v.verification_id}`, { verification_status: status, change_note: `Status set to ${status}` });
      toast.success(`Marked ${status}`);
      fetchRows();
    } catch { toast.error('Update failed'); }
  };

  const remove = async (v) => {
    if (!window.confirm('Remove this verification record?')) return;
    try {
      await axios.delete(`${API_URL}/admin/license-verifications/${v.verification_id}`);
      toast.success('Removed');
      fetchRows();
    } catch { toast.error('Delete failed'); }
  };

  const exportCsv = () => {
    window.open(`${API_URL}/admin/license-verifications/export`, '_blank');
  };

  const runTexasImport = async () => {
    setTxImporting(true);
    try {
      const res = await axios.post(`${API_URL}/admin/license-verifications/texas-import`, { csv_url: txUrl || undefined });
      toast.success(`Texas BHEC: ${res.data.verified} verified, ${res.data.needs_review} need review, ${res.data.unable_to_verify} unable, ${res.data.not_found} not found`);
      fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Import failed');
    } finally {
      setTxImporting(false);
    }
  };

  const VisualBadge = ({ v }) => {
    const cfg = VISUAL[v?.display?.visual_status] || VISUAL.gray;
    const Icon = cfg.icon;
    return <Badge className={`${cfg.cls} text-xs`}><Icon className="w-3 h-3 mr-1" />{cfg.label}</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="license-verification-center">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
            <BadgeCheck className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">License Verification Center</h3>
            <p className="text-sm text-gray-500">Verify and manage social worker licenses against official state boards.</p>
          </div>
        </div>
        <Button variant="outline" onClick={exportCsv} data-testid="export-verifications-btn">
          <Download className="w-4 h-4 mr-2" />Export CSV
        </Button>
      </div>

      {/* Summary */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
          {[
            { k: 'total', label: 'Total', color: 'text-gray-900' },
            { k: 'pending', label: 'Pending', color: 'text-gray-600' },
            { k: 'expiring_30', label: 'Exp ≤30d', color: 'text-amber-600' },
            { k: 'expiring_60', label: 'Exp ≤60d', color: 'text-amber-500' },
            { k: 'expiring_90', label: 'Exp ≤90d', color: 'text-yellow-500' },
            { k: 'expired', label: 'Expired', color: 'text-red-600' },
            { k: 'overdue', label: 'Reverify', color: 'text-red-500' },
          ].map((s) => (
            <div key={s.k} className="bg-white rounded-lg border border-gray-100 p-3 text-center">
              <div className={`text-2xl font-bold ${s.color}`}>{summary[s.k] ?? 0}</div>
              <div className="text-xs text-gray-500">{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Search applicants + Texas import */}
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <h4 className="font-medium text-gray-900 mb-2">Find a social worker to verify</h4>
          <div className="flex gap-2">
            <Input placeholder="Search by name, email, state, license type" value={applicantQuery}
              onChange={(e) => setApplicantQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && searchApplicants()}
              data-testid="applicant-search-input" />
            <Button onClick={searchApplicants} disabled={searching} data-testid="applicant-search-btn">
              {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            </Button>
          </div>
          <div className="mt-3 max-h-48 overflow-y-auto divide-y">
            {applicants.map((a) => (
              <div key={a.user_id} className="flex items-center justify-between py-2">
                <div>
                  <p className="text-sm font-medium text-gray-900">{a.name}</p>
                  <p className="text-xs text-gray-500">{a.email} · {a.license_type || 'no license'}</p>
                </div>
                <Button size="sm" variant="outline" onClick={() => openEditorForApplicant(a)} data-testid={`verify-applicant-${a.user_id}`}>
                  {a.verification ? 'Review' : 'Verify'}
                </Button>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <h4 className="font-medium text-gray-900 mb-2">Texas TBHEC bulk match</h4>
          <p className="text-xs text-gray-500 mb-2">Matches TX candidates against the official BHEC social work licensee file (updated every 24h) by license number + rank + name. Runs automatically daily.</p>
          <div className="flex gap-2">
            <Input placeholder="https://.../social-work-licensees.csv" value={txUrl}
              onChange={(e) => setTxUrl(e.target.value)} data-testid="texas-csv-url-input" />
            <Button onClick={runTexasImport} disabled={txImporting} className="bg-primary hover:bg-primary-hover text-white" data-testid="texas-import-btn">
              {txImporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-100 p-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[180px]">
            <Label className="text-xs">Search</Label>
            <Input placeholder="Name / license #" value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })} data-testid="verif-filter-search" />
          </div>
          <div>
            <Label className="text-xs">Status</Label>
            <Select value={filters.verification_status} onValueChange={(v) => setFilters({ ...filters, verification_status: v })}>
              <SelectTrigger className="w-40" data-testid="verif-filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {V_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">License</Label>
            <Select value={filters.license_type} onValueChange={(v) => setFilters({ ...filters, license_type: v })}>
              <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {LICENSE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Expiring</Label>
            <Select value={String(filters.expiring_days)} onValueChange={(v) => setFilters({ ...filters, expiring_days: v })}>
              <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any</SelectItem>
                <SelectItem value="30">≤ 30 days</SelectItem>
                <SelectItem value="60">≤ 60 days</SelectItem>
                <SelectItem value="90">≤ 90 days</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" onClick={() => setFilters({ ...filters, overdue: !filters.overdue })}
            className={filters.overdue ? 'border-red-400 text-red-600' : ''} data-testid="verif-filter-overdue">
            <RefreshCw className="w-4 h-4 mr-1" />Overdue only
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="p-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400" /></div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-gray-500">No verification records yet. Search for a social worker above to begin.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table w-full">
              <thead><tr>
                <th>Name</th><th>License</th><th>State</th><th>Number</th><th>Expires</th><th>Status</th><th>Actions</th>
              </tr></thead>
              <tbody>
                {rows.map((v) => (
                  <tr key={v.verification_id} data-testid={`verif-row-${v.verification_id}`}>
                    <td className="font-medium">{v.legal_first_name} {v.legal_last_name}<div className="text-xs text-gray-400">{v.applicant_email}</div></td>
                    <td>{v.license_type}</td>
                    <td>{v.state}</td>
                    <td className="font-mono text-sm">{v.display_full_number ? v.license_number : (v.license_number ? '••••' + v.license_number.slice(-4) : '—')}</td>
                    <td>{v.expiration_date ? String(v.expiration_date).slice(0, 10) : '—'}</td>
                    <td><VisualBadge v={v} /></td>
                    <td>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openEditorForVerification(v)} title="Edit"><Edit3 className="w-4 h-4" /></Button>
                        {v.official_source_url && (
                          <a href={v.official_source_url} target="_blank" rel="noopener noreferrer">
                            <Button variant="ghost" size="sm" title="Official source"><ExternalLink className="w-4 h-4" /></Button>
                          </a>
                        )}
                        <Button variant="ghost" size="sm" className="text-amber-600" onClick={() => quickStatus(v, 'suspended')} title="Suspend"><AlertTriangle className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="sm" className="text-red-500" onClick={() => remove(v)} title="Remove"><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Editor Dialog */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle className="font-serif">{editingId ? 'Review / Update Verification' : 'New License Verification'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3 text-sm">
              <span className="font-medium">{form.applicant_name}</span> · {form.applicant_email}
            </div>

            {form.official_source_url && (
              <a href={form.official_source_url} target="_blank" rel="noopener noreferrer">
                <Button type="button" variant="outline" className="w-full" data-testid="open-official-source-btn">
                  Open Official State Verification Site <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              </a>
            )}

            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">Legal First</Label><Input value={form.legal_first_name} onChange={(e) => setForm({ ...form, legal_first_name: e.target.value })} data-testid="v-first" /></div>
              <div><Label className="text-xs">Middle</Label><Input value={form.legal_middle_name} onChange={(e) => setForm({ ...form, legal_middle_name: e.target.value })} /></div>
              <div><Label className="text-xs">Legal Last</Label><Input value={form.legal_last_name} onChange={(e) => setForm({ ...form, legal_last_name: e.target.value })} data-testid="v-last" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">State</Label><Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} /></div>
              <div>
                <Label className="text-xs">License Type</Label>
                <Select value={form.license_type} onValueChange={(v) => setForm({ ...form, license_type: v })}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{LICENSE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">License Number</Label><Input value={form.license_number} onChange={(e) => setForm({ ...form, license_number: e.target.value })} data-testid="v-number" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">Original Issue</Label><Input type="date" value={(form.original_issue_date || '').slice(0,10)} onChange={(e) => setForm({ ...form, original_issue_date: e.target.value })} /></div>
              <div><Label className="text-xs">Expiration</Label><Input type="date" value={(form.expiration_date || '').slice(0,10)} onChange={(e) => setForm({ ...form, expiration_date: e.target.value })} data-testid="v-expiration" /></div>
              <div><Label className="text-xs">Next Reverify</Label><Input type="date" value={(form.next_reverification_date || '').slice(0,10)} onChange={(e) => setForm({ ...form, next_reverification_date: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">License Status</Label>
                <Select value={form.license_status} onValueChange={(v) => setForm({ ...form, license_status: v })}>
                  <SelectTrigger data-testid="v-license-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{L_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Verification Status</Label>
                <Select value={form.verification_status} onValueChange={(v) => setForm({ ...form, verification_status: v })}>
                  <SelectTrigger data-testid="v-verification-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{V_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Official Source Name</Label><Input value={form.official_source_name} onChange={(e) => setForm({ ...form, official_source_name: e.target.value })} /></div>
              <div><Label className="text-xs">Official Source URL</Label><Input value={form.official_source_url} onChange={(e) => setForm({ ...form, official_source_url: e.target.value })} /></div>
            </div>
            <div><Label className="text-xs">Admin Notes</Label><Textarea rows={2} value={form.admin_notes} onChange={(e) => setForm({ ...form, admin_notes: e.target.value })} /></div>
            <div className="flex items-center justify-between">
              <Label className="text-sm">Show full license number publicly</Label>
              <Switch checked={form.display_full_number} onCheckedChange={(c) => setForm({ ...form, display_full_number: c })} data-testid="v-display-full" />
            </div>

            {audit.length > 0 && (
              <div className="border-t pt-3">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2"><History className="w-4 h-4" />Audit history</div>
                <div className="space-y-1 max-h-32 overflow-y-auto text-xs text-gray-500">
                  {audit.map((a) => (
                    <div key={a.audit_id}>{String(a.created_at).slice(0,16).replace('T',' ')} — <strong>{a.action}</strong> {a.previous_status ? `(${a.previous_status}→${a.new_status})` : ''} {a.description}</div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditorOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-verification-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <BadgeCheck className="w-4 h-4 mr-2" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminTabVerification;
